import { MoreVert, } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CachedIcon from '@mui/icons-material/Cached';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button, CircularProgress, ClickAwayListener, Divider, Fade, IconButton, Paper, Popper, Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as DocumentIcon } from "../../../assets/icons/docs-2.svg";
import { ReactComponent as FileUploadIcon } from "../../../assets/icons/upload.svg";
import { AuthContext } from '../../../contexts/AuthContext';
import { DashboardContext } from '../../../contexts/DashboardContext';
import { ExportableFields } from '../../../contexts/DashboardsContext/DashboardsProvider';
import useDashboard from '../../../hooks/useDashboard';
import useDashboards from '../../../hooks/useDashboards';
import useRoute from '../../../hooks/useRoute';
import theme from '../../../theme';
import { DashboardQuery } from '../../../types/files';
import ConfirmDialog from '../../modals/ConfirmDialog';
import DashboardExportPDFModal from '../../modals/dashboard-details/DashboardExportPDFModal';
import FileUploaderModal from '../../modals/dashboard-details/FileUploaderModal';
import FindInvestorsShareDealModal from '../../modals/dashboard-details/FindInvestorsShareDealModal';
import ExportDocument from '../dashboard-export/ExportDocument';

const useStyles = makeStyles((theme) => ({
    modal: {
        width: 'fit-content',
        height: 'fit-content',
        padding: '0 8px',
        border: '1px solid lightgray',
        borderRadius: 8,
    },
    title: {
        fontFamily: 'Poppins',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: 'black',
        marginLeft: 8,
    },
    popupButton: {
        minWidth: 120,
        width: 'auto',
        height: 'fit-content',
        borderRadius: 32,
        border: `1px solid ${theme.colors.primary['200']}`,
        background: '#E0F1F1',
        color: theme.palette.primary.main,
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        "& > .MuiButton-endIcon": {
            "& > .MuiSvgIcon-root": {
                width: 32,
                height: 32,
            }
        },
    },
    button: {
        width: '100%',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: 'black',
        textTransform: 'none',
        justifyContent: 'flex-start',
        "& > .MuiButton-startIcon": {
            "& > .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                color: theme.palette.primary.light,
            }
        },
    },
    donwloadIcon: {
        minWidth: 22,
        width: 22,
        minHeight: 22,
        height: 22,
        stroke: 'black',
    },
    syncExternally: {
        width: '100%',
        padding: '2px 8px !important',
        height: 40,
        borderRadius: 8,
        color: '#0f766e',
        background: '#f0fdfa',
        border: `1.5px solid #99f6e4`,
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        "& > .MuiButton-startIcon": {
            paddingTop: 2,
        },
    },
    synced: {
        width: '18ch',
        padding: 'unset',
        "& > .MuiButton-startIcon": {
            paddingTop: 2,
        },
        "&.Mui-disabled": {
            color: '#059669',
            background: 'transparent',
            fontFamily: 'Lato',
            fontSize: '0.9rem',
            fontWeight: 'bold',
            textTransform: 'none',
        },
    },
    uploadIcon: {
        width: 22,
        height: 22,
        stroke: 'black',
    },
}));

export const DEF_VALID_FILE = { maxFileSize: 100 * 1024 * 1024, allowedFileTypes: ['application/pdf'], };

const CompanyMultiAction: React.FC<{ flags?: any, minimal?: boolean }> = ({ flags, minimal  }) => {
    const classes = useStyles();
    const { userGroup: author } = useContext(AuthContext);
    const { dashboardQueries, defaultQueryTemplateValues, dashboard } = useContext(DashboardContext);
    const { redirectFromDashboard } = useRoute();
    const { deleteDashboard } = useDashboards();
    const { autoSync } = useDashboard();

    const [popperOpen, setPopperOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<string>('');
    const [findShareModalOpen, setFindShareModalOpen] = useState<boolean>(false);
    const [exportPDFModalOpen, setExportPDFModalOpen] = useState<boolean>(false);
    const [fileUploadModalOpen, setFileUploadModalOpen] = useState<boolean>(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
    const [syncExternally, setSyncExternally] = useState<boolean>(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const filteredQueries = useMemo(() => ExportableFields.map(field => {
            const lookUpQuery = dashboardQueries.find(query => query.title === field);

            if (!!lookUpQuery) {
                return ({...lookUpQuery,
                    title: (defaultQueryTemplateValues
                        .find(defQuery => defQuery?.title === lookUpQuery.title)?.displayTitle || lookUpQuery.title)
                            .replace('2.0', ''),
                    order: defaultQueryTemplateValues.find(defQuery => defQuery.title === lookUpQuery.title)?.order || lookUpQuery.order
                });
            }

            return null;
        }).filter(query => !!query) as DashboardQuery[]
    , [dashboardQueries, defaultQueryTemplateValues]);

    const handleSyncExternal = useCallback((checked: boolean) => {
        setLoading('sync');
        autoSync(checked).finally(() => {
            setLoading('');
            setSyncExternally(checked);
        });
    }, [autoSync]);

    const handleDelete = useCallback(() => {
        setLoading('delete');
        deleteDashboard(dashboard!.id).finally(() => {
            setLoading('');
            redirectFromDashboard();
        });
    }, [dashboard, deleteDashboard, redirectFromDashboard]);

    useEffect(() => {
        if (flags.zapierPlan === false) {
            if (dashboard?.shouldSyncExternally === true)
                handleSyncExternal(false);
        } else if (dashboard) {
            setSyncExternally(!!dashboard?.shouldSyncExternally);
        }
    }, [flags.zapierPlan, dashboard, handleSyncExternal]);

    return (<>
        {minimal ? (
          <IconButton onClick={() => setPopperOpen(prev => !prev)} ref={anchorRef}>
            <MoreVert />
          </IconButton>
        ) : (
            <Button variant="outlined"
                className={classes.popupButton}
                endIcon={<ArrowDropDownIcon />}
                onClick={() => setPopperOpen(prev => !prev)}
                ref={anchorRef} >
                {'Actions'}
            </Button>
        )}
        <Popper open={popperOpen}
            placement="bottom-end"
            anchorEl={anchorRef.current}
            sx={{ zIndex: 1500 }}
            transition>
            {({ TransitionProps }) => (
                <Fade {...TransitionProps}>
                    <Paper className={classes.modal} elevation={3} square={false}>
                        <ClickAwayListener onClickAway={() => setPopperOpen(false)}>
                            <Stack direction="column" alignItems="flex-start" justifyContent="center" width="100%" my={1}>
                                <Stack alignItems="center" justifyContent="center" width="100%" py={1}>
                                    {!syncExternally ? (
                                        <Button variant="outlined"
                                            className={classes.syncExternally}
                                            startIcon={(loading === 'sync') ? (
                                                <CircularProgress size={24} sx={{ color: 'black' }} />
                                            ): (<CachedIcon sx={{ fontSize: "28px !important", color: theme.colors.cyan['500'] }} />)}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                handleSyncExternal(true);
                                            }} disableRipple>{'Sync externally'} </Button>
                                    ) : (
                                        <Button variant="text"
                                            className={classes.synced}
                                            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "18px !important", color: '#059669' }} />}
                                            disabled> {'Sync complete'} </Button>
                                    )}
                                </Stack>
                                <Divider sx={{ width: '100%' }}/>
                                <PDFDownloadLink document={
                                    <ExportDocument bulkDashboards={[{dashboard: dashboard!, queries: filteredQueries}]} author={author} />
                                } fileName={`${dashboard!.title}.pdf`}>
                                    {() => (
                                        <Button className={classes.button}
                                            startIcon={(loading === 'export-pdf') ? (
                                                <CircularProgress size="24px" sx={{ color: 'black' }} />
                                            ): (<DocumentIcon className={classes.donwloadIcon} />)}
                                        > {'Generate one-pager (PDF)'} </Button>
                                    )}
                                </PDFDownloadLink>
                                <Button className={classes.button}
                                    startIcon={<FileUploadIcon className={classes.uploadIcon} />}
                                    onClick={(e: any) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setFileUploadModalOpen(true);
                                        setPopperOpen(false);
                                    }}
                                > {'Add files'} </Button>
                                <Divider sx={{ width: '100%', margin: '4px 0 !important' }}/>
                                <Button variant="text"
                                    className={classes.button}
                                    startIcon={<DeleteOutlineIcon
                                        sx={{ color: `${theme.colors.error['500']} !important` }} />}
                                    onClick={(e: any) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setConfirmDeleteOpen(true);
                                    }}
                                    sx={{ color: `${theme.colors.error['500']} !important` }}
                                > {'Delete'} </Button>
                            </Stack>
                        </ClickAwayListener>
                    </Paper>
                </Fade>
            )}
        </Popper>
        {findShareModalOpen && (
            <FindInvestorsShareDealModal
                isOpen={findShareModalOpen}
                onClose={() => setFindShareModalOpen(false)} />
        )}
        {fileUploadModalOpen && (
            <FileUploaderModal
                open={fileUploadModalOpen}
                validFile={DEF_VALID_FILE}
                validPath={!!dashboard ? [dashboard.id, dashboard.title].join('/') : undefined}
                onClose={() => setFileUploadModalOpen(false)}
            />
        )}
        {exportPDFModalOpen && (
            <DashboardExportPDFModal
                isOpen={exportPDFModalOpen}
                onClose={() => setExportPDFModalOpen(false)}
            />
        )}
        <ConfirmDialog
            title="Delete confirmation"
            content={`Are you sure you want to delete ${dashboard?.title}?`}
            open={confirmDeleteOpen}
            loading={loading === 'delete'}
            confirmCallback={handleDelete}
            cancelCallback={() => setConfirmDeleteOpen(false)}
        />
    </>);
}

export default withLDConsumer()(CompanyMultiAction);